/**
 * AddSectionSidebar - Kimi-style left sidebar
 * 
 * Exact Kimi UI parity:
 * - "ADD SECTION" header
 * - Simple list: icon + section name (no drag handles, no visibility toggles)
 * - "+ Custom" button at bottom
 * - Clean, minimal design
 */
import React from 'react';
import { 
  Layout, Type, Image, Grid, Users, Sparkles, 
  BookOpen, Award, Megaphone, MessageSquare,
  LayoutGrid, ImageIcon, UserCircle, BarChart3, MessageCircle, Plus
} from 'lucide-react';

// Section type definitions matching Kimi sidebar
const SECTION_TYPES = [
  { id: 'hero', name: 'Hero', icon: Layout },
  { id: 'content', name: 'Content', icon: Type },
  { id: 'media_grid', name: 'Media Grid', icon: LayoutGrid },
  { id: 'comic_panel', name: 'Comic Panel', icon: Grid },
  { id: 'chapter', name: 'Chapter', icon: BookOpen },
  { id: 'gallery', name: 'Gallery', icon: ImageIcon },
  { id: 'profile_header', name: 'Profile Header', icon: UserCircle },
  { id: 'stats', name: 'Stats', icon: BarChart3 },
  { id: 'comments', name: 'Comments', icon: MessageCircle },
];

// Map existing block types to Kimi section types
const BLOCK_TO_SECTION_MAP = {
  homepage_hero_carousel: 'hero',
  homepage_trending: 'content',
  homepage_featured_series: 'media_grid',
  homepage_new_releases: 'content',
  homepage_featured_creators: 'content',
  homepage_featured_artworks: 'gallery',
  homepage_cta_banner: 'content',
  landing_hero: 'hero',
  landing_content_types: 'content',
  landing_benefits: 'content',
  landing_build_phase: 'content',
  landing_creators: 'content',
  landing_transparency: 'content',
};

const getSectionType = (blockType) => {
  const mappedType = BLOCK_TO_SECTION_MAP[blockType];
  if (mappedType) {
    return SECTION_TYPES.find(s => s.id === mappedType);
  }
  // Try prefix match
  const prefix = blockType?.split('_')[0];
  return SECTION_TYPES.find(s => s.id === prefix) || SECTION_TYPES[0];
};

const AddSectionSidebar = ({
  sections = [],
  selectedSectionId,
  onSelectSection,
  onAddSection,
}) => {
  // Get unique section types from current page
  const currentSectionTypes = sections.map(s => {
    const sectionType = getSectionType(s.type);
    return {
      ...s,
      sectionType,
      displayName: sectionType?.name || s.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      IconComponent: sectionType?.icon || Layout,
    };
  });

  return (
    <div 
      className="w-[140px] bg-zinc-950 border-r border-zinc-800/30 flex flex-col h-full"
      data-testid="add-section-sidebar"
    >
      {/* Header */}
      <div className="px-4 py-4 border-b border-zinc-800/30">
        <h2 className="text-[11px] font-medium text-zinc-400 tracking-wide">
          ADD SECTION
        </h2>
      </div>
      
      {/* Section type list */}
      <div className="flex-1 overflow-y-auto py-2">
        {SECTION_TYPES.map((sectionType) => {
          const Icon = sectionType.icon;
          // Check if this section type exists on the current page
          const existsOnPage = currentSectionTypes.some(s => s.sectionType?.id === sectionType.id);
          
          return (
            <button
              key={sectionType.id}
              onClick={() => onAddSection(sectionType.id)}
              className={`
                w-full flex items-center gap-3 px-4 py-2.5 text-left
                transition-colors duration-150
                ${existsOnPage 
                  ? 'text-zinc-300' 
                  : 'text-zinc-500 hover:text-zinc-300'
                }
                hover:bg-zinc-900/50
              `}
              data-testid={`add-section-${sectionType.id}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="text-[13px]">{sectionType.name}</span>
            </button>
          );
        })}
      </div>
      
      {/* Custom button */}
      <div className="p-3 border-t border-zinc-800/30">
        <button
          onClick={() => onAddSection('custom')}
          className="w-full flex items-center gap-2 px-3 py-2 
                     text-[13px] text-zinc-500 hover:text-zinc-300
                     border border-dashed border-zinc-800 hover:border-zinc-600
                     rounded transition-colors"
          data-testid="add-custom-section"
        >
          <Plus className="w-4 h-4" />
          <span>Custom</span>
        </button>
      </div>
    </div>
  );
};

export default AddSectionSidebar;
